import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Mail, Smartphone, Globe, Info } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Tooltip from '../../components/ui/Tooltip';
import { useNotificationTemplatesApi } from './config';

// Task 33 (Sekme 3) — 21 olay kataloğu (NotificationCatalog.cs — TEK kaynak,
// grep ile doğrulanmış) + seçili olayda konu/gövde override editörü. Boş
// konu/gövde ile kaydetmek sunucuda reddedilir (subjectRequired/bodyRequired);
// varsayılana dönmenin gerçek yolu "Varsayılana Dön" (DELETE) butonudur — bkz.
// config/index.js.resetToDefault yorumu.

const Layout = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const ListPane = styled.div`
  width: 320px;
  flex-shrink: 0;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  background: #fff;
  max-height: calc(100vh - 260px);
  overflow-y: auto;
`;

const GroupLabel = styled.div`
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 700;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: #F5F7FA;
`;

const EventRow = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f2f4f7;
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.06)' : 'transparent')};
  &:hover { background: rgba(2, 145, 239, 0.04); }
`;

const EventName = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
`;

const BADGE_TONES = {
  critical: { bg: 'rgba(229, 57, 53, 0.1)', fg: '#E53935' },
  custom: { bg: 'rgba(2, 211, 191, 0.12)', fg: '#02A899' },
  off: { bg: 'rgba(108, 117, 125, 0.12)', fg: '#6c757d' },
};

const Badge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  background: ${({ $tone }) => BADGE_TONES[$tone]?.bg || BADGE_TONES.custom.bg};
  color: ${({ $tone }) => BADGE_TONES[$tone]?.fg || BADGE_TONES.custom.fg};
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const RecipientsHint = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: -8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const RecipientsText = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DetailPane = styled.div`
  flex: 1;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 20px;
  min-width: 0;
`;

const DetailTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const ChannelsHint = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TokenHint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin-top: -4px;
  margin-bottom: 16px;
`;

// Olay başına token beyanı (sunucu: NotificationCatalog.DeclaredTokens → `tokens`).
// Beyan eden olayda YALNIZ gerçekten doldurulan değişkenler gösterilir — genel
// ipucu PM dilindedir ({{employee}} …) ve adaya giden bir şablonda kiracıyı
// doldurulmayan bir token yazmaya yönlendirirdi. Token adları ÇEVİRİ DEĞİL, sabit
// JSX literalidir (i18next {{...}} interpolasyonuyla çakışmasın — Şirket
// Ayarları'ndaki kutlama şablonu ipucuyla aynı desen).
const formatTokens = (tokens) => tokens.map((token) => `{{${token}}}`).join(' ');

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 40px;
`;

// 2026-08-04: "portal" kanalı katalogdan kaldırıldı (uygulama-içi bildirim
// merkezi yok; gösterilmesi yapılmayan bir teslimi vaat ediyordu). Globe,
// bilinmeyen bir kanal gelirse diye yedek ikon olarak kalır (satır 196).
const CHANNEL_ICONS = { email: Mail, app: Smartphone };

export default function NotificationTemplatesTab() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const {
    data, fetching, saving, save, resetToDefault, setEnabled,
  } = useNotificationTemplatesApi();
  const [selectedKey, setSelectedKey] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);
  // Kritik olayı KAPATMA onayı bekleyen olay anahtarı (açmak onay istemez).
  const [disableConfirmKey, setDisableConfirmKey] = useState(null);
  const [toggling, setToggling] = useState(false);

  const selected = useMemo(() => data.find((d) => d.eventKey === selectedKey) || null, [data, selectedKey]);

  // PARTİ 19 — DÜZENLEME YETKİSİ OLAYIN MODÜLÜNE BAĞLI, sabit `PM.Write`e değil.
  // Katalog PARTİ 12'de modül-nötr oldu ve sunucu kapısı da olay başına taşındı;
  // burada `canWrite('PM')` kalsaydı ATS'i olup PM'i OLMAYAN kiracının İK
  // uzmanı ekranı açar, listeyi görür ama HER ŞEY salt-okunur olurdu —
  // sunucu izin verirken arayüzün kilitlemesi, çıkmazın kılık değiştirmiş hâli.
  // Eşleme sunucudaki NotificationCatalog.ModuleCodeForEventKey ile AYNIdır.
  const moduleCodeOf = useCallback((eventKey) => {
    if (!eventKey) return null;
    if (eventKey.startsWith('pm.')) return 'PM';
    if (eventKey.startsWith('td.')) return 'TD';
    if (eventKey.startsWith('ats.')) return 'AT';
    // Public API Plan 03 — webhook ucu otomatik kapatıldı bildirimi PA modülündedir.
    if (eventKey.startsWith('pa.')) return 'PA';
    return null;
  }, []);

  const canManage = useMemo(() => {
    const code = moduleCodeOf(selected?.eventKey);
    return code ? canWrite(code) : false;
  }, [selected, moduleCodeOf, canWrite]);
  const pmEvents = useMemo(() => data.filter((d) => d.eventKey.startsWith('pm.')), [data]);
  const tdEvents = useMemo(() => data.filter((d) => d.eventKey.startsWith('td.')), [data]);
  // PARTİ 12 (Faz 3, N-01) — ATS ailesi. Uç katalogun TAMAMINI döndürüyordu;
  // burada yalnız iki önek render edildiği için yeni bir modülün olayları
  // SESSİZCE DÜŞÜYORDU: kiracı ATS metinlerini ne görebilir ne düzenleyebilirdi
  // (N-01'in kusur tanımının tam karşılığı). Aşağıdaki "gruplanmamış" bloğu da
  // bu yüzden var: bir sonraki modül eklendiğinde olaylar yine kaybolmasın.
  const atsEvents = useMemo(() => data.filter((d) => d.eventKey.startsWith('ats.')), [data]);
  const otherEvents = useMemo(
    () => data.filter((d) => !['pm.', 'td.', 'ats.'].some((p) => d.eventKey.startsWith(p))),
    [data],
  );

  const handleSelect = useCallback((item) => {
    setSelectedKey(item.eventKey);
    setSubject(item.subject);
    setBody(item.body);
  }, []);

  // Kaydet/varsayılana dön sonrası liste yenilenir — seçili olayın alanlarını
  // sunucudaki efektif değerle senkron tutar.
  useEffect(() => {
    if (selected) {
      setSubject(selected.subject);
      setBody(selected.body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.subject, selected?.body]);

  const handleSave = async () => {
    if (!selected) return;
    await save(selected.eventKey, { subject, body });
  };

  const confirmReset = async () => {
    if (!selected) return;
    await resetToDefault(selected.eventKey);
    setResetConfirm(false);
  };

  const applyEnabled = async (eventKey, isEnabled) => {
    setToggling(true);
    try {
      await setEnabled(eventKey, isEnabled);
    } finally {
      setToggling(false);
    }
  };

  // Kritik olaylar onay bekleyen kayıtları haber verir: kapatılırsa iş akışı
  // sessizce durabilir. Bu yüzden KAPATMA önce onay ister; açma istemez.
  const handleToggleEnabled = (item, nextEnabled) => {
    if (!nextEnabled && item.isCritical) {
      setDisableConfirmKey(item.eventKey);
      return;
    }
    applyEnabled(item.eventKey, nextEnabled);
  };

  const confirmDisable = async () => {
    const key = disableConfirmKey;
    setDisableConfirmKey(null);
    if (key) await applyEnabled(key, false);
  };

  // Satır yoksa sunucu `isEnabled: true` döner; alan hiç gelmezse de açık say.
  const isEnabledOf = (item) => item.isEnabled !== false;

  const renderEvent = (item) => (
    <EventRow key={item.eventKey} $active={item.eventKey === selectedKey} onClick={() => handleSelect(item)}>
      <EventName $active={item.eventKey === selectedKey} $disabled={!isEnabledOf(item)}>
        {t(`performance.notification.event.${item.eventKey}`)}
      </EventName>
      <BadgeRow>
        {!isEnabledOf(item) && <Badge $tone="off">{t('performance.settings.notifications.disabledBadge')}</Badge>}
        {item.isCritical && <Badge $tone="critical">{t('performance.settings.notifications.criticalBadge')}</Badge>}
        {item.isOverridden && <Badge $tone="custom">{t('performance.settings.notifications.overriddenBadge')}</Badge>}
      </BadgeRow>
    </EventRow>
  );

  return (
    <Layout>
      <ListPane>
        <GroupLabel>{t('performance.settings.notifications.groupPm')}</GroupLabel>
        {pmEvents.map(renderEvent)}
        <GroupLabel>{t('performance.settings.notifications.groupTd')}</GroupLabel>
        {tdEvents.map(renderEvent)}
        {atsEvents.length > 0 && (
          <>
            <GroupLabel>{t('performance.settings.notifications.groupAts')}</GroupLabel>
            {atsEvents.map(renderEvent)}
          </>
        )}
        {otherEvents.length > 0 && (
          <>
            <GroupLabel>{t('performance.settings.notifications.groupOther')}</GroupLabel>
            {otherEvents.map(renderEvent)}
          </>
        )}
      </ListPane>

      <DetailPane>
        {!selected ? (
          <EmptyText>{fetching ? t('common.loading') : t('performance.settings.notifications.selectEventHint')}</EmptyText>
        ) : (
          <>
            <DetailHeader>
              <DetailTitle>{t(`performance.notification.event.${selected.eventKey}`)}</DetailTitle>
              <CheckboxInput
                label={t('performance.settings.notifications.enabledLabel')}
                checked={isEnabledOf(selected)}
                onChange={(next) => handleToggleEnabled(selected, next)}
                disabled={!canManage || toggling}
              />
            </DetailHeader>
            {Array.isArray(selected.recipients) && selected.recipients.length > 0 && (
              <RecipientsHint>
                {t('performance.settings.notifications.recipientsLabel')}:
                <RecipientsText>
                  {selected.recipients
                    .map((code) => t(`performance.settings.notifications.audience.${code}`, { defaultValue: code }))
                    .join(', ')}
                </RecipientsText>
                <Tooltip text={t('performance.settings.notifications.recipientsHint')}>
                  <Info size={13} aria-label={t('performance.settings.notifications.recipientsHint')} />
                </Tooltip>
              </RecipientsHint>
            )}
            <ChannelsHint>
              {t('performance.settings.notifications.channelsLabel')}:
              {selected.channels.map((c) => {
                const Icon = CHANNEL_ICONS[c] || Globe;
                return <Icon key={c} size={14} title={t(`performance.settings.notifications.channel.${c}`)} />;
              })}
            </ChannelsHint>

            <FieldGroup>
              <TextInput
                label={t('performance.settings.notifications.subjectLabel')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={!canManage}
              />
            </FieldGroup>
            <FieldGroup>
              <TextInput
                label={t('performance.settings.notifications.bodyLabel')}
                type="textarea"
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={!canManage}
              />
            </FieldGroup>
            {Array.isArray(selected.tokens) && selected.tokens.length > 0 ? (
              <TokenHint data-role="declared-tokens">
                {t('performance.settings.notifications.availableTokensLabel')}{' '}
                <code>{formatTokens(selected.tokens)}</code>
              </TokenHint>
            ) : (
              <TokenHint>{t('performance.settings.notifications.tokenHint')}</TokenHint>
            )}

            {canManage && (
              <Actions>
                <Button
                  variant="outline"
                  onClick={() => setResetConfirm(true)}
                  disabled={saving || !selected.isOverridden}
                >
                  {t('performance.settings.notifications.resetToDefault')}
                </Button>
                <Button onClick={handleSave} disabled={saving || !subject.trim() || !body.trim()} loading={saving}>
                  {saving ? t('common.saving') : t('common.save')}
                </Button>
              </Actions>
            )}
          </>
        )}
      </DetailPane>

      <ConfirmDialog
        isOpen={resetConfirm}
        title={t('performance.settings.notifications.resetConfirmTitle')}
        message={t('performance.settings.notifications.resetConfirmMessage')}
        onConfirm={confirmReset}
        onCancel={() => setResetConfirm(false)}
        loading={saving}
      />

      <ConfirmDialog
        isOpen={!!disableConfirmKey}
        title={t('performance.settings.notifications.disableCriticalTitle')}
        message={t('performance.settings.notifications.disableCriticalMessage')}
        confirmText={t('performance.settings.notifications.disableCriticalConfirm')}
        onConfirm={confirmDisable}
        onCancel={() => setDisableConfirmKey(null)}
      />
    </Layout>
  );
}
